/**
 * Os números da Etapa 7.
 *
 * Três perguntas, e nada além: quantas conversas começaram, quantas terminaram, e **em qual bloco a
 * pessoa desistiu**. A terceira é a que muda um fluxo, e é a razão de o SDK escutar a página saindo
 * para mandar o evento por `sendBeacon`.
 *
 * Não há gráfico aqui de propósito. Com contadores por dia e uma tabela por bloco, a leitura é
 * direta; um gráfico exigiria uma dependência nova para dizer a mesma coisa com menos precisão.
 *
 * O que **não** aparece aqui: nada digitado pelo visitante. Isso vive no painel de Contatos, que é um
 * canal separado e desligável.
 *
 * A tela larga é o que permitiu o "por dia" virar tabela. Nos 384px da coluna lateral cada dia era
 * uma fileira de três selos coloridos que estourava a largura — informação empilhada, não comparável.
 * Em colunas alinhadas, dá para varrer trinta dias e achar o pico.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditor } from '../../store/editor.js';
import { getDataSource, isDemoMode, type AnalyticsDay } from '../../data/index.js';
import { BLOCK_META } from '../../flow/mapping.js';
import { EmptyState, IconButton, Notice, Section, Stat } from '../ui.js';
import { IconeAtualizar, IconeDados } from '../icons.js';

interface LinhaBloco {
  nodeId: string;
  label: string;
  tipo: string | null;
  entradas: number;
  abandonos: number;
}

function somar(dias: AnalyticsDay[]): {
  starts: number;
  completions: number;
  abandons: number;
  nodeEnters: Record<string, number>;
  nodeAbandons: Record<string, number>;
} {
  const total = {
    starts: 0,
    completions: 0,
    abandons: 0,
    nodeEnters: {} as Record<string, number>,
    nodeAbandons: {} as Record<string, number>,
  };
  for (const dia of dias) {
    total.starts += dia.starts;
    total.completions += dia.completions;
    total.abandons += dia.abandons;
    for (const [nodeId, n] of Object.entries(dia.nodeEnters ?? {})) {
      total.nodeEnters[nodeId] = (total.nodeEnters[nodeId] ?? 0) + n;
    }
    for (const [nodeId, n] of Object.entries(dia.nodeAbandons ?? {})) {
      total.nodeAbandons[nodeId] = (total.nodeAbandons[nodeId] ?? 0) + n;
    }
  }
  return total;
}

/*
 * Cabeçalho e célula numérica saem daqui para as duas tabelas ficarem iguais sem repetição de
 * classe. `tabular-nums` para as colunas de número alinharem na vertical — sem isso, "1" e "9"
 * ocupam larguras diferentes e a coluna serpenteia.
 */
const TH = 'px-4 py-2 text-xs font-semibold tracking-wide text-slate-500 uppercase';
const TD_NUM = 'px-4 py-3 text-right text-sm tabular-nums';

export function AnalyticsPanel() {
  const id = useEditor((state) => state.id);
  const nodes = useEditor((state) => state.nodes);
  const [dias, setDias] = useState<AnalyticsDay[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    if (!id) return;
    setCarregando(true);
    setErro(null);
    try {
      setDias(await getDataSource().getAnalytics(id, 30));
    } catch (caught) {
      setErro(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const total = useMemo(() => somar(dias), [dias]);

  /*
   * A tabela junta o número com o bloco que existe hoje no rascunho. Bloco já excluído continua
   * aparecendo, marcado — apagar o registro histórico ao apagar o bloco esconderia justamente o
   * ponto onde as pessoas desistiam.
   */
  const linhas = useMemo<LinhaBloco[]>(() => {
    const ids = new Set([...Object.keys(total.nodeEnters), ...Object.keys(total.nodeAbandons)]);
    const porId = new Map(nodes.map((node) => [node.id, node]));

    return [...ids]
      .map((nodeId) => {
        const node = porId.get(nodeId);
        const bloco = node?.data.block as { label?: string } | undefined;
        return {
          nodeId,
          label: bloco?.label ?? nodeId,
          tipo: node ? (BLOCK_META[node.type as keyof typeof BLOCK_META]?.label ?? null) : null,
          entradas: total.nodeEnters[nodeId] ?? 0,
          abandonos: total.nodeAbandons[nodeId] ?? 0,
        };
      })
      .sort((a, b) => b.abandonos - a.abandonos || b.entradas - a.entradas);
  }, [total, nodes]);

  const taxa = total.starts > 0 ? Math.round((total.completions / total.starts) * 100) : null;

  if (isDemoMode) {
    return (
      <Notice tone="info" title="Sem números no modo demo">
        Os eventos vêm do widget rodando num site de verdade. O preview do painel não envia nada —
        conversa de teste não pode virar métrica de produção.
      </Notice>
    );
  }

  return (
    <div className="space-y-6">
      {erro ? (
        <Notice tone="danger" title="Não foi possível carregar">
          {erro}
        </Notice>
      ) : null}

      <Section
        icon={IconeDados}
        title="Últimos 30 dias"
        description="Contados a partir dos eventos que o widget envia do site do cliente."
        action={<IconButton icon={IconeAtualizar} label="Atualizar números" onClick={() => void carregar()} />}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Stat label="Iniciadas" value={total.starts.toLocaleString('pt-BR')} />
          <Stat label="Concluídas" value={total.completions.toLocaleString('pt-BR')} />
          <Stat label="Abandonadas" value={total.abandons.toLocaleString('pt-BR')} />
          <Stat
            label="Taxa de conclusão"
            value={taxa === null ? '—' : `${taxa}%`}
            hint={taxa === null ? 'Sem conversas iniciadas ainda.' : undefined}
          />
        </div>
      </Section>

      <Section
        title="Por bloco"
        description="Abandono é onde a pessoa fechou a página sem terminar. Muitos abandonos e poucas passagens é o candidato a ser reescrito."
        padded={false}
      >
        {carregando ? (
          <p className="p-4 text-sm text-slate-500">Carregando…</p>
        ) : linhas.length === 0 ? (
          <EmptyState
            icon={IconeDados}
            title="Nenhum evento ainda"
            description="Publique o webchat, instale a tag num site autorizado e converse uma vez. Os números aparecem aqui no dia seguinte à primeira conversa."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className={TH}>Bloco</th>
                  <th className={`${TH} text-right`}>Passagens</th>
                  <th className={`${TH} text-right`}>Abandonos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {linhas.map((linha) => (
                  <tr key={linha.nodeId}>
                    <td className="px-4 py-3">
                      <span className="block truncate text-sm font-medium text-slate-800">{linha.label}</span>
                      <span className="text-xs text-slate-500">
                        {linha.tipo ?? 'bloco removido do fluxo'}
                      </span>
                    </td>
                    <td className={`${TD_NUM} text-slate-700`}>{linha.entradas}</td>
                    <td
                      className={`${TD_NUM} font-semibold ${
                        linha.abandonos > 0 ? 'text-red-700' : 'text-slate-400'
                      }`}
                    >
                      {linha.abandonos}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {dias.length > 0 ? (
        <Section title="Por dia" padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className={TH}>Dia</th>
                  <th className={`${TH} text-right`}>Iniciadas</th>
                  <th className={`${TH} text-right`}>Concluídas</th>
                  <th className={`${TH} text-right`}>Abandonos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dias.map((dia) => (
                  <tr key={dia.day}>
                    <td className="px-4 py-3 font-mono text-sm text-slate-600">{dia.day}</td>
                    <td className={`${TD_NUM} text-slate-700`}>{dia.starts}</td>
                    <td className={`${TD_NUM} text-slate-700`}>{dia.completions}</td>
                    <td className={`${TD_NUM} ${dia.abandons > 0 ? 'text-red-700' : 'text-slate-400'}`}>
                      {dia.abandons}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}
    </div>
  );
}
